import apiBaseUrl from '../../utils/config.js';

class AuthorizationService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/patientauthorization`;
  }

  getToken() {
    return localStorage.getItem("jwt");
  }

  getAuthHeaders() {
    const token = this.getToken();

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async #request(endpoint, method = 'GET', body = null) {
    const url = `${this.baseUrl}${endpoint}`;

    const options = {
      method,
      headers: this.getAuthHeaders(),
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, options);

      const text = await response.text();

      let data = null;

      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      }

      if (!response.ok) {
        const message =
          data?.message ||
          data?.error ||
          data ||
          `Request failed with status ${response.status}`;

        throw new Error(message);
      }

      return data;
    } catch (error) {
      console.error(`Error in API request: ${method} ${url}`, error);
      throw error;
    }
  }

  async addPatientAuthorization(data) {
    return this.#request('/add', 'POST', data);
  }

  async editAuthorization(authId, updatedAuthorization) {
    return this.#request(`/edit/${authId}`, 'PUT', updatedAuthorization);
  }

  async editSpecificColumn(authId, columnName, value) {
    const params = new URLSearchParams({
      columnName,
      value,
    });

    return this.#request(`/edit-column/${authId}?${params.toString()}`, 'PATCH');
  }

  async removeAuthorization(authId) {
    return this.#request(`/remove/${authId}`, 'DELETE');
  }

  async findByPatientId(patientId) {
    return this.#request(`/find-by-patient/${patientId}`);
  }

  async findByAuthId(authId) {
    return this.#request(`/find-by-auth/${authId}`);
  }

  async getLatestAuthId(patientId) {
    return this.#request(`/latest-auth/${patientId}`);
  }

  async getInsptIdByAuthId(authId) {
    return this.#request(`/insptId/${authId}`);
  }

  /*
   * New:
   * totalVisits = original approved visit count.
   * It does not count down.
   */
  async getTotalVisitsByAuthId(authId) {
    return this.#request(`/total-visits/${authId}`);
  }

  /*
   * Optional helper:
   * updates the permanent total visits only.
   */
  async updateTotalVisits(authId, totalVisits) {
    return this.editSpecificColumn(authId, 'totalVisits', totalVisits);
  }

  /*
   * Optional helper:
   * updates remaining visits only.
   */
  async updateRemainingVisits(authId, visits) {
    return this.editSpecificColumn(authId, 'visits', visits);
  }
}

export default AuthorizationService;